import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { useState } from 'react';

interface MultiValueInputProps {
  label: string;
  placeholder?: string;
  type?: 'text' | 'number';
  value: Array<string>;
  onChange: (value: Array<string>) => void;
  error?: string;
  maxItems?: number;
  addedItemsTitle?: string;
  className?: string;
}

export function MultiValueInput({
  label,
  placeholder,
  type = 'text',
  value = [],
  onChange,
  error,
  maxItems = 3,
  addedItemsTitle = 'Added Items',
  className,
}: MultiValueInputProps) {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    if (!inputValue.trim()) return;
    if (value.length >= maxItems) return;
    onChange([...value, inputValue.trim()]);
    setInputValue('');
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className={`flex flex-col gap-4 ${className || ''}`}>
      <div className="flex items-end gap-2">
        <Input
          id={`multi-value-input-${label}`}
          type={type}
          label={`${label} ${value.length}/${maxItems}`}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="bg-white"
          placeholder={placeholder}
          disabled={value.length >= maxItems}
          error={error}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button
          type="button"
          variant="primary"
          onClick={handleAdd}
          className="mb-[2px]"
          disabled={value.length >= maxItems}
        >
          Add
        </Button>
      </div>

      {value.length > 0 && (
        <div className="flex flex-col gap-2 rounded-md bg-gray-100 p-3">
          <span className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
            {addedItemsTitle}
          </span>
          <div className="flex flex-col gap-2">
            {value.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between pb-2"
              >
                <span className="text-sm font-medium text-slate-700">
                  {item}
                </span>
                <RelationActionButton
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="text-delete"
                >
                  Remove
                </RelationActionButton>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
